import React, { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Header from "../../components/content_wrapper/header";
import CustomLoader from "../../components/customLoader";
import NoRecordFound from "../../components/noRecordFound";
import Pagination from "../../utils/pagination";
import {
  useKeyHostRequests,
  useKeyHostRequestAnalytics,
  useUpdateKeyHostRequest,
} from "../../hooks/keyHostRequests/useKeyHostRequests";

// The API returns snake_case; the axios response interceptor camelCases it, so
// every field below is read camelCase (number_of_keys -> numberOfKeys, etc.).

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "fulfilled", label: "Fulfilled" },
  { key: "cancelled", label: "Cancelled" },
  { key: "expired", label: "Expired" },
  { key: "", label: "All" },
];

const STATUS_COLOUR: Record<string, string> = {
  pending: "#e67e22",
  fulfilled: "#27ae60",
  cancelled: "#7f8c8d",
  expired: "#e74c3c",
};

function StatusPill({ status }: { status?: string }) {
  const colour = STATUS_COLOUR[status || ""] || "#7f8c8d";
  return (
    <span
      style={{
        background: colour,
        color: "#fff",
        padding: "2px 9px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {status || "—"}
    </span>
  );
}

const KeyHostRequests: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState("pending");

  const { data, isLoading, refetch } = useKeyHostRequests({ page, limit, status });
  const { data: analyticsRes } = useKeyHostRequestAnalytics();
  const update = useUpdateKeyHostRequest();

  const rows: any[] = data?.data?.requests ?? [];
  const pagination = data?.data?.pagination ?? { total: 0, totalPages: 1 };
  const stats = analyticsRes?.data ?? null;

  const act = (r: any, next: "fulfilled" | "cancelled" | "expired") => {
    const verb =
      next === "fulfilled" ? "fulfilled" : next === "cancelled" ? "cancelled" : "expired";
    if (
      !window.confirm(
        `Mark this request from ${r.user?.email || "unknown"} (${r.address || "no address"}) as ${verb}?`
      )
    )
      return;
    update.mutate(
      { id: r._id, status: next },
      {
        onSuccess: () => {
          toast.success(`Request marked ${verb}`);
          refetch();
        },
        onError: () => {
          toast.error("Could not update the request");
        },
      }
    );
  };

  // The app sends `address` as a reverse-geocoded street address when the owner
  // searches a place, but as a bare "lat, lng" string when they use the map
  // centre. Treat the latter as "no address" so it isn't shown twice.
  const isCoordString = (s?: string) =>
    !!s && /^\s*-?\d{1,3}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?\s*$/.test(s);

  const locationOf = (r: any) => {
    const c = r.location?.coordinates;
    if (!Array.isArray(c) || c.length !== 2) return null;
    const lat = Number(c[1]);
    const lng = Number(c[0]);
    return {
      lat,
      lng,
      mapsUrl: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
    };
  };

  return (
    <div className="main-wrapper">
      <Header title="Key Host Requests" />
      <div className="content-wrapper">
        <CustomLoader loader={isLoading} />
        <section className="main-section spacer">
          <div className="container">
            <p className="text-muted mb-3">
              Owners asking for a key host <strong>near an address</strong> where there
              isn't one yet (from the app's "Request a key host near me"). Different from{" "}
              <em>Host Slot Requests</em>, which is owners waitlisted at a specific full
              host. Mark <em>Fulfilled</em> once a host is placed/assigned in the area.
            </p>

            {stats && (
              <div className="row g-3 mb-3">
                {[
                  { label: "Pending", value: stats.pendingRequests, colour: "#e67e22" },
                  { label: "Fulfilled", value: stats.fulfilledRequests, colour: "#27ae60" },
                  { label: "Cancelled", value: stats.cancelledRequests, colour: "#7f8c8d" },
                  { label: "Expired", value: stats.expiredRequests, colour: "#e74c3c" },
                ].map((c) => (
                  <div className="col-6 col-md-3" key={c.label}>
                    <div className="card h-100" style={{ borderLeft: `4px solid ${c.colour}` }}>
                      <div className="card-body py-3 px-3">
                        <div
                          className="text-muted"
                          style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}
                        >
                          {c.label}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{c.value ?? 0}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(stats?.topRequestedAreas?.length ?? 0) > 0 && (
              <div className="wrapper-card mb-3 p-3">
                <div
                  className="text-muted mb-2"
                  style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}
                >
                  Where demand is concentrated (pending)
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {stats.topRequestedAreas.map((a: any, i: number) => (
                    <a
                      key={i}
                      className="badge bg-light text-dark border text-decoration-none"
                      style={{ fontSize: 12, fontWeight: 500 }}
                      href={`https://www.google.com/maps/search/?api=1&query=${a.averageLatitude},${a.averageLongitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {a.count}× · {a.address || `${Number(a.averageLatitude).toFixed(3)}, ${Number(a.averageLongitude).toFixed(3)}`} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}

            <ul className="nav nav-pills mb-3" style={{ gap: 6 }}>
              {STATUS_TABS.map((t) => (
                <li className="nav-item" key={t.key || "all"}>
                  <button
                    className={`nav-link ${status === t.key ? "active" : ""}`}
                    onClick={() => {
                      setStatus(t.key);
                      setPage(1);
                    }}
                  >
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>

            {!isLoading && rows.length === 0 ? (
              <NoRecordFound />
            ) : (
              <>
                <div className="table-responsive wrapper-card">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>Owner</th>
                        <th>Location</th>
                        <th>Keys</th>
                        <th>Type</th>
                        <th>Needed by</th>
                        <th>Notes</th>
                        <th>Status</th>
                        <th>Requested</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r._id}>
                          <td>
                            <div className="fw-semibold">{r.user?.name || "—"}</div>
                            <div className="text-muted small">{r.user?.email || "—"}</div>
                          </td>
                          <td style={{ maxWidth: 260 }}>
                            {r.address && !isCoordString(r.address) ? (
                              <div>{r.address}</div>
                            ) : (
                              <div className="text-muted fst-italic">
                                No street address given
                              </div>
                            )}
                            {(() => {
                              const loc = locationOf(r);
                              return loc ? (
                                <a
                                  className="small"
                                  href={loc.mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)} — view on map ↗
                                </a>
                              ) : (
                                <span className="text-muted small">no coordinates</span>
                              );
                            })()}
                          </td>
                          <td>{r.numberOfKeys ?? "—"}</td>
                          <td className="text-capitalize">{r.locationType || "—"}</td>
                          <td>
                            {r.dateNeeded
                              ? moment(r.dateNeeded).format("MMM D, YYYY")
                              : "—"}
                          </td>
                          <td style={{ maxWidth: 200 }} className="text-muted small">
                            {r.notes || "—"}
                          </td>
                          <td>
                            <StatusPill status={r.status} />
                            {r.fulfilledAt && (
                              <div className="text-muted small">
                                {moment(r.fulfilledAt).format("MMM D")}
                              </div>
                            )}
                          </td>
                          <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                            {r.createdAt ? moment(r.createdAt).fromNow() : "—"}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {r.status === "pending" && (
                              <>
                                <button
                                  className="btn btn-sm btn-success me-1"
                                  disabled={update.isLoading}
                                  onClick={() => act(r, "fulfilled")}
                                >
                                  Fulfil
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  disabled={update.isLoading}
                                  onClick={() => act(r, "cancelled")}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={update.isLoading}
                                  onClick={() => act(r, "expired")}
                                >
                                  Expire
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={page}
                  totalPages={pagination.totalPages || 1}
                  handleNextPage={() => setPage((p) => p + 1)}
                  handlePrevPage={() => setPage((p) => Math.max(1, p - 1))}
                  handleRowLimitChange={(v: number) => {
                    setLimit(v);
                    setPage(1);
                  }}
                />
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default KeyHostRequests;
